import {jsonArrayMember, jsonMember, jsonObject} from "typedjson";
import {DxGridDataType} from "../../dx-grid-data-type";


/**
 * Тип данных колонки грида.
 * Объект создан больше для гуппировки полей, связанных с типизацией колонки и ее рендерингом. Чтобы не было огромной
 * и запутанной портянки в `DxGridColumn`.
 *
 * Объект один на все типы из-за ограничения десериализации. В классе `DxGridColumn` поле `dataType` имеет тип
 * `DxGridColumnDataType`, поэтому потомок без указания дополнительной информации будет десериализовываться как
 * базовый тип. Это можно победить добавление в JSON `__type: <Класс потомка>`. Но это лишние сложности с точки
 * зрения конфигурации.
 */
@jsonObject
export class DxGridColumnDataType {
  /** Идент типа данных. По умолчанию строка. */
  @jsonMember({name: "typeIdent", constructor: String, emitDefaultValue: true, isRequired: false })
  private _typeIdent: string = DxGridDataType.TEXT;

  /** Строка с маской форматирования: даты, времени, даты и времени, числа и тд. */
  @jsonMember({name: "format", constructor: String, emitDefaultValue: false, isRequired: false })
  private _format: string;

  /** Опциональная иконка ссылки. */
  @jsonMember({name: "linkIcon", constructor: String, emitDefaultValue: false, isRequired: false })
  private _linkIcon: string;

  /**
   * Паттерн url (относительной или абсолютной) или произвольная url.
   * Паттерн описвается в виде:
   * ```
   * it-events\event\${id}?param_1=${param_1_val}&param_2=${param_2_val}
   * ```
   * Если url начинается с http:\https: то текущее расположение к ней добавляться не будет.
   */
  @jsonMember({name: "linkPattern", constructor: String, emitDefaultValue: false, isRequired: false })
  private _linkPattern: string;

  /** признак того, что ссылка на документ. Нужен, чтобы можно было открывать файлы минуя роутер */
  @jsonMember({name: "linkToDoc", constructor: Boolean, emitDefaultValue: false, isRequired: false })
  private _linkToDoc: boolean = false;

  /** Открывать ссылку в новом окне или нет */
  @jsonMember({name: "newWindow", constructor: Boolean, emitDefaultValue: false, isRequired: false })
  private _newWindow: boolean = false;


  /**
   * Наименования параметров url.
   * 1. Чтобы не выкалупывать наименования параметров из паттерна.
   * 2. Имена параметров должны совпадать с именами геттров в объектах. По ним будет
   *    извлекаться значения из объекта строки.
   */
  @jsonArrayMember(String, {name: "linkParams", emitDefaultValue: false, isRequired: false })
  private _linkParams: string[];

  /** Идент  справочника. */
  @jsonMember({name: "dictIdent", constructor: String, emitDefaultValue: false, isRequired: false })
  private _dictIdent: string;

  /** Количество типов после запятой для типов CURRENCY и DOUBLE. */
  @jsonMember({name: "accuracy", constructor: Number, emitDefaultValue: false, isRequired: false })
  private _accuracy: number = 2;

  /** подсвечивать отрицательные значения для типа CURRENCY */
  @jsonMember({name: "highlightNegatives", constructor: Boolean, emitDefaultValue: false, isRequired: false })
  private _highlightNegatives: boolean = false;


  get isText(): boolean {
    return DxGridDataType.TEXT === this._typeIdent;
  }

  get isLargeText(): boolean {
    return DxGridDataType.LARGE_TEXT === this._typeIdent;
  }

  get isInt(): boolean {
    return DxGridDataType.INT === this._typeIdent;
  }

  get isDouble(): boolean {
    return DxGridDataType.DOUBLE === this._typeIdent;
  }

  get isCurrency(): boolean {
    return DxGridDataType.CURRENCY === this._typeIdent;
  }

  get isDictionary(): boolean {
    return DxGridDataType.DICTIONARY === this._typeIdent;
  }

  get isDate(): boolean {
    return DxGridDataType.DATE === this._typeIdent;
  }

  get isTime(): boolean {
    return DxGridDataType.TIME === this._typeIdent;
  }

  get isDateTime(): boolean {
    return DxGridDataType.DATE_TIME === this._typeIdent;
  }

  get isLink(): boolean {
    return DxGridDataType.LINK === this._typeIdent;
  }

  get isCheckbox(): boolean {
    return DxGridDataType.CHECKBOX === this._typeIdent;
  }

  get typeIdent(): string {
    return this._typeIdent;
  }

  set typeIdent(value: string) {
    this._typeIdent = value;
  }

  get format(): string {
    return this._format;
  }

  set format(value: string) {
    this._format = value;
  }

  get linkIcon(): string {
    return this._linkIcon;
  }

  set linkIcon(value: string) {
    this._linkIcon = value;
  }

  get linkPattern(): string {
    return this._linkPattern;
  }

  set linkPattern(value: string) {
    this._linkPattern = value;
  }

  get linkParams(): string[] {
    return this._linkParams;
  }

  set linkParams(value: string[]) {
    this._linkParams = value;
  }

  get dictIdent(): string {
    return this._dictIdent;
  }

  set dictIdent(value: string) {
    this._dictIdent = value;
  }

  get accuracy(): number {
    return this._accuracy;
  }

  get newWindow(): boolean {
    return this._newWindow;
  }

  set newWindow(value: boolean) {
    this._newWindow = value;
  }

  get linkToDoc(): boolean {
    return this._linkToDoc;
  }

  set linkToDoc(value: boolean) {
    this._linkToDoc = value;
  }

  get highlightNegatives(): boolean {
    return this._highlightNegatives;
  }

  set highlightNegatives(value: boolean) {
    this._highlightNegatives = value;
  }
}
